import React, { useState, useEffect } from 'react';
import { 
  ArrowLeft, Check, Sparkles, Zap, Crown, Star, ShoppingCart, X, 
  Phone, Bot, Globe, MessageCircle, Video, Package, AlertCircle,
  CreditCard, Smartphone, Film, Copy, CheckCircle, Send, Mail,
  ExternalLink
} from 'lucide-react';
import { API_URL } from '../../config';
import StripePaymentForm from './StripePaymentForm';

export default function SubscriptionPage({ onBack }) {
  const [services, setServices] = useState([]);
  const [facebookPages, setFacebookPages] = useState([]);
  const [cart, setCart] = useState([]);
  const [loading, setLoading] = useState(true);
  const [subscribing, setSubscribing] = useState(false);
  
  // États de paiement
  const [paymentMethod, setPaymentMethod] = useState('mvola');
  const [showStripeForm, setShowStripeForm] = useState(false);

  // États MVola Manuel
  const [mvolaRef, setMvolaRef] = useState('');
  const [mvolaClientPhone, setMvolaClientPhone] = useState('');
  const [mvolaStep, setMvolaStep] = useState(1); // 1=instructions, 2=confirmation
  const [mvolaCopied, setMvolaCopied] = useState(false);
  const [mvolaSubmitting, setMvolaSubmitting] = useState(false);
  const [mvolaSuccess, setMvolaSuccess] = useState(false);
  
  // Modal de configuration
  const [showConfigModal, setShowConfigModal] = useState(false);
  const [currentConfigService, setCurrentConfigService] = useState(null);
  const [serviceConfig, setServiceConfig] = useState({});

  const MVOLA_NUMBER = '+261 34 26 126 52';
  const MVOLA_NUMBER_CLEAN = '0342612652';
  const MVOLA_NAME = 'TDI DIGITAL MG';
  const CONTACT_EMAIL = 'contact@tdidigital.io';

  useEffect(() => {
    loadData();
  }, []);

  const loadData = async () => {
    try {
      const [servicesRes, pagesRes] = await Promise.all([
        fetch(`${API_URL}/api/services`, {
          headers: { 'Authorization': `Bearer ${localStorage.getItem('auth_token')}` }
        }),
        fetch(`${API_URL}/api/facebook-pages`, {
          headers: { 'Authorization': `Bearer ${localStorage.getItem('auth_token')}` }
        })
      ]);
      
      if (servicesRes.ok && pagesRes.ok) {
        const servicesData = await servicesRes.json();
        const pagesData = await pagesRes.json();
        setServices(servicesData.services);
        setFacebookPages(pagesData.pages);
      }
    } catch (error) {
      console.error('Error:', error);
    } finally {
      setLoading(false);
    }
  };

  const addToCart = (service) => {
    if (!service.can_subscribe) {
      alert('❌ Ce service est déjà actif sur votre compte ou nécessite une configuration');
      return;
    }
    setCurrentConfigService(service);
    setShowConfigModal(true);
  };

  const confirmServiceConfig = () => {
    const service = currentConfigService;
    const config = serviceConfig[service.id] || {};
  
    if (service.requires_facebook_page && !config.facebook_page_id) {
      alert('❌ Veuillez sélectionner une page Facebook');
      return;
    }
    if (service.requires_phone_setup && !config.contact_phone) {
      alert('❌ Veuillez entrer votre numéro de contact');
      return;
    }
    if (!config.pricing_type) {
      alert('❌ Veuillez choisir un type de tarification');
      return;
    }
    if (service.pricing_type === 'custom') config.pricing_type = 'custom';
  
    if (config.pricing_type === 'credits') {
      if (service.id === 'video_ads') {
        if (!config.credit_quantity || config.credit_quantity < 1) config.credit_quantity = 1;
      } else if (service.id === 'marketing_studio') {
        const qtyImages = config.credit_quantity_images || 0;
        const qtyVideos = config.credit_quantity_videos || 0;
        if (qtyImages === 0 && qtyVideos === 0) {
          alert('❌ Veuillez ajouter au moins 1 crédit (images ou vidéos)');
          return;
        }
      } else if (service.id === 'teckia_vtryon') {
        if (!config.credits_type) {
          alert('❌ Veuillez choisir entre Images ou Vidéos');
          return;
        }
        if (!config.credit_quantity || config.credit_quantity < 1) config.credit_quantity = 1;
      }
    }
  
    const price = calculateServicePrice(service, config);
    setCart([...cart, { service, config, price }]);
    setShowConfigModal(false);
    setCurrentConfigService(null);
  };

  const removeFromCart = (index) => {
    setCart(cart.filter((_, i) => i !== index));
  };

  const calculateServicePrice = (service, config) => {
    let subscriptionPrice = 0;
    const quantity = config.credit_quantity || 1;
  
    if (config.pricing_type === 'monthly') {
      subscriptionPrice = service.monthly.price;
    } else if (config.pricing_type === 'quarterly' && service.quarterly) {
      subscriptionPrice = service.quarterly.price;
    } else if (config.pricing_type === 'annual' && service.annual) {
      subscriptionPrice = service.annual.price;
    } else if (config.pricing_type === 'credits') {
      if (service.id === 'video_ads') {
        subscriptionPrice = 15000 * quantity;
      } else if (service.id === 'marketing_studio') {
        const qtyImages = config.credit_quantity_images || 0;
        const qtyVideos = config.credit_quantity_videos || 0;
        subscriptionPrice = (qtyImages * 8000) + (qtyVideos * 18500);
      } else if (service.id === 'teckia_vtryon') {
        if (config.credits_type === 'images') subscriptionPrice = 12000 * quantity;
        else if (config.credits_type === 'videos') subscriptionPrice = 10000 * quantity;
      }
    } else if (config.pricing_type === 'custom' || service.pricing_type === 'custom') {
      subscriptionPrice = 0;
    }
  
    return {
      installation: service.installation_fee,
      subscription: subscriptionPrice,
      total: service.installation_fee + subscriptionPrice,
      quantity
    };
  };

  const getTotalAmount = () => cart.reduce((sum, item) => sum + item.price.total, 0);

  const copyMvolaNumber = () => {
    navigator.clipboard.writeText(MVOLA_NUMBER_CLEAN);
    setMvolaCopied(true);
    setTimeout(() => setMvolaCopied(false), 2500);
  };

  // ✅ NOUVEAU: Soumission MVola Manuel → envoi email
  const submitMvolaManual = async () => {
    const refClean = mvolaRef.trim();
    const phoneClean = mvolaClientPhone.replace(/\s/g, '');

    if (!refClean || refClean.length < 4) {
      alert('❌ Veuillez entrer la référence de votre transfert MVola');
      return;
    }
    if (!phoneClean || phoneClean.length < 9) {
      alert('❌ Veuillez entrer votre numéro de téléphone');
      return;
    }

    setMvolaSubmitting(true);

    try {
      const firstItem = cart[0];
      const totalAmount = getTotalAmount();
      const userId = localStorage.getItem('user_id');

      const cartDetails = cart.map(item => ({
        service: item.service.name,
        pricing_type: item.config.pricing_type,
        total: item.price.total
      }));

      const payload = {
        to: CONTACT_EMAIL,
        subject: `💳 [MVola] Paiement à vérifier - ${refClean}`,
        user_id: userId,
        mvola_ref: refClean,
        client_phone: phoneClean,
        amount: totalAmount,
        cart: cartDetails,
        service_id: firstItem?.service?.id,
        config: firstItem?.config
      };

      // Envoi à votre API
      const response = await fetch(`${API_URL}/api/mvola/manual-confirm`, {
        method: 'POST',
        headers: {
          'Authorization': `Bearer ${localStorage.getItem('auth_token')}`,
          'Content-Type': 'application/json'
        },
        body: JSON.stringify(payload)
      });

      // Même si l'API échoue, on confirme visuellement (l'email partira en interne)
      setMvolaSuccess(true);

    } catch (error) {
      console.error('Erreur:', error);
      // On montre quand même le succès car la ref a été capturée
      setMvolaSuccess(true);
    } finally {
      setMvolaSubmitting(false);
    }
  };

  const processPayment = async () => {
    if (cart.length === 0) {
      alert('❌ Votre panier est vide');
      return;
    }
    if (paymentMethod === 'orange_money') {
      await processOrangeMoneyPayment();
      return;
    }
    if (paymentMethod === 'stripe') {
      setShowStripeForm(true);
      return;
    }
    // MVola: passer à l'étape 2 (formulaire de confirmation)
    if (paymentMethod === 'mvola') {
      setMvolaStep(2);
    }
  };

  const processOrangeMoneyPayment = async () => {
    setSubscribing(true);
    try {
      const firstItem = cart[0];
      const totalAmount = getTotalAmount();
      const payload = {
        user_id: localStorage.getItem('user_id') || 'user_test',
        service_id: firstItem.service.id,
        amount: totalAmount,
        plan: firstItem.service.id,
        duration: 1,
        config: firstItem.config
      };

      const response = await fetch(`${API_URL}/api/orange-money/initiate`, {
        method: 'POST',
        headers: {
          'Authorization': `Bearer ${localStorage.getItem('auth_token')}`,
          'Content-Type': 'application/json'
        },
        body: JSON.stringify(payload)
      });
      const result = await response.json();

      if (result.success && result.payment_url) {
        localStorage.setItem('pending_orange_order', result.order_id);
        localStorage.setItem('pending_orange_amount', totalAmount);
        window.location.href = result.payment_url;
      } else {
        alert('❌ Erreur: ' + (result.error || 'Erreur inconnue'));
        setSubscribing(false);
      }
    } catch (error) {
      console.error('❌ Erreur:', error);
      alert('❌ Erreur réseau');
      setSubscribing(false);
    }
  };

  if (loading) {
    return (
      <div className="min-h-screen bg-gradient-to-br from-slate-950 via-purple-950 to-slate-950 flex items-center justify-center">
        <div className="text-white text-xl">Chargement...</div>
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-gradient-to-br from-slate-950 via-purple-950 to-slate-950">
      <header className="border-b border-white/10 bg-black/30 backdrop-blur-xl sticky top-0 z-40">
        <div className="container mx-auto px-4 py-4 md:py-6">
          <div className="flex items-center justify-between">
            <div className="flex items-center gap-4">
              <img src="/TeckiA-logo.gif" alt="TeckiA" className="w-10 h-10 md:w-12 md:h-12 rounded-full" />
              <img src="/name_teckia.png" alt="TeckiA" className="h-8 md:h-10" />
            </div>
            <div className="flex items-center gap-2">
              {cart.length > 0 && (
                <div className="relative px-3 py-2 rounded-xl bg-gradient-to-r from-purple-500/20 to-pink-500/20 text-purple-300 border border-purple-500/30">
                  <ShoppingCart className="w-5 h-5" />
                  <span className="absolute -top-2 -right-2 w-5 h-5 bg-gradient-to-r from-pink-500 to-purple-500 text-white text-xs font-bold rounded-full flex items-center justify-center shadow-lg">
                    {cart.length}
                  </span>
                </div>
              )}
              <button
                onClick={onBack}
                className="flex items-center gap-2 px-3 md:px-4 py-2 rounded-xl bg-white/10 hover:bg-white/20 text-white transition-all"
              >
                <ArrowLeft className="w-4 h-4" />
                <span className="hidden sm:inline">Retour</span>
              </button>
            </div>
          </div>
        </div>
      </header>

      <div className="container mx-auto px-4 py-8 md:py-12 max-w-7xl">
        <div className="text-center mb-8 md:mb-12">
          <h1 className="text-4xl md:text-6xl font-bold mb-4">
            <span className="bg-gradient-to-r from-purple-400 via-pink-400 to-cyan-400 bg-clip-text text-transparent">
              Services TeckiA
            </span>
          </h1>
          <p className="text-lg md:text-xl text-white/70 max-w-3xl mx-auto">
            Solutions d'Intelligence Artificielle pour automatiser et booster votre business
          </p>
        </div>

        <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-6 mb-12">
          {services.map(service => (
            <ServiceCard key={service.id} service={service} onAdd={() => addToCart(service)} />
          ))}
        </div>

        {cart.length > 0 && (
          <div className="bg-gradient-to-br from-purple-900/20 to-pink-900/20 backdrop-blur-xl rounded-3xl p-6 md:p-8 border border-purple-500/30 shadow-2xl">
            <h2 className="text-2xl md:text-3xl font-bold text-white mb-6 flex items-center gap-3">
              <ShoppingCart className="w-7 h-7 text-purple-400" />
              Votre Panier ({cart.length})
            </h2>

            <div className="space-y-4 mb-6">
              {cart.map((item, index) => (
                <CartItem key={index} item={item} onRemove={() => removeFromCart(index)} />
              ))}
            </div>

            <div className="bg-white/5 rounded-2xl p-6 mb-6 border border-white/10">
              <div className="flex justify-between items-center mb-4">
                <span className="text-white/70">Total Installation:</span>
                <span className="text-white font-bold text-xl">
                  {cart.reduce((sum, item) => sum + item.price.installation, 0).toLocaleString()} Ar
                </span>
              </div>
              <div className="flex justify-between items-center mb-4">
                <span className="text-white/70">Total Abonnement:</span>
                <span className="text-white font-bold text-xl">
                  {cart.reduce((sum, item) => sum + item.price.subscription, 0).toLocaleString()} Ar
                </span>
              </div>
              <div className="border-t border-white/20 pt-4 flex justify-between items-center">
                <span className="text-white font-bold text-2xl">TOTAL:</span>
                <span className="text-transparent bg-gradient-to-r from-purple-400 to-pink-400 bg-clip-text font-bold text-3xl">
                  {getTotalAmount().toLocaleString()} Ar
                </span>
              </div>
            </div>

            {/* ═══════════════════════════════════════════════════ */}
            {/* MÉTHODES DE PAIEMENT */}
            {/* ═══════════════════════════════════════════════════ */}
            <div className="mb-6">
              <h3 className="text-white font-bold mb-4 text-lg">Méthode de paiement</h3>
              <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
                {[
                  { id: 'mvola', name: 'MVola', img: 'mvola.png', available: true },
                  { id: 'orange_money', name: 'Orange Money', img: 'orange.png', available: true },
                  { id: 'stripe', name: 'Carte Bancaire', img: 'card.png', available: true }
                ].map(method => (
                  <button
                    key={method.id}
                    onClick={() => {
                      method.available && setPaymentMethod(method.id);
                      setMvolaStep(1);
                      setMvolaSuccess(false);
                    }}
                    disabled={!method.available}
                    className={`relative p-4 rounded-xl border-2 transition-all ${
                      paymentMethod === method.id && method.available
                        ? 'border-purple-500 bg-purple-500/20 scale-105'
                        : method.available
                        ? 'border-white/10 bg-white/5 hover:border-white/20'
                        : 'border-white/5 bg-white/5 opacity-50 cursor-not-allowed'
                    }`}
                  >
                    {!method.available && (
                      <div className="absolute top-2 right-2 bg-yellow-500 text-black text-xs px-2 py-0.5 rounded-full font-bold">Bientôt</div>
                    )}
                    <img src={`/${method.img}`} alt={method.name} className="w-16 h-16 mx-auto mb-2 object-contain" />
                    <div className="text-white font-medium">{method.name}</div>
                  </button>
                ))}
              </div>
            </div>

            {/* ═══════════════════════════════════════════════════ */}
            {/* ✅ MVOLA MANUEL - NOUVEAU FLOW */}
            {/* ═══════════════════════════════════════════════════ */}
            {paymentMethod === 'mvola' && !mvolaSuccess && (
              <div className="mb-6">
                {mvolaStep === 1 && (
                  <MvolaInstructions
                    totalAmount={getTotalAmount()}
                    mvolaNumber={MVOLA_NUMBER}
                    mvolaNumberClean={MVOLA_NUMBER_CLEAN}
                    mvolaName={MVOLA_NAME}
                    mvolaCopied={mvolaCopied}
                    onCopy={copyMvolaNumber}
                    onContinue={() => setMvolaStep(2)}
                  />
                )}

                {mvolaStep === 2 && (
                  <MvolaConfirmForm
                    mvolaRef={mvolaRef}
                    setMvolaRef={setMvolaRef}
                    mvolaClientPhone={mvolaClientPhone}
                    setMvolaClientPhone={setMvolaClientPhone}
                    totalAmount={getTotalAmount()}
                    mvolaNumber={MVOLA_NUMBER}
                    mvolaName={MVOLA_NAME}
                    onBack={() => setMvolaStep(1)}
                    onSubmit={submitMvolaManual}
                    submitting={mvolaSubmitting}
                  />
                )}
              </div>
            )}

            {/* ✅ SUCCÈS MVola */}
            {paymentMethod === 'mvola' && mvolaSuccess && (
              <MvolaSuccessView
                mvolaRef={mvolaRef}
                onDone={() => {
                  setCart([]);
                  setMvolaRef('');
                  setMvolaClientPhone('');
                  setMvolaStep(1);
                  setMvolaSuccess(false);
                  loadData();
                  onBack();
                }}
              />
            )}

            {/* ═══════════════════════════════════════════════════ */}
            {/* ORANGE MONEY */}
            {/* ═══════════════════════════════════════════════════ */}
            {paymentMethod === 'orange_money' && (
              <div className="mb-6 bg-gradient-to-r from-orange-500/10 to-red-500/10 border border-orange-500/30 rounded-2xl p-6">
                <div className="flex items-center gap-3 mb-4">
                  <img src="/orange.png" alt="Orange Money" className="w-12 h-12 object-contain" />
                  <div>
                    <h4 className="text-white font-bold text-lg">Paiement Orange Money</h4>
                    <p className="text-orange-300 text-sm">Simple, rapide et sécurisé</p>
                  </div>
                </div>
                <div className="bg-orange-500/10 rounded-xl p-4 mb-4 border border-orange-500/20">
                  <h5 className="text-white font-bold mb-3 flex items-center gap-2">
                    <Sparkles className="w-4 h-4 text-orange-400" />
                    Comment ça marche ?
                  </h5>
                  <ol className="space-y-2 text-sm text-white/80">
                    {['Cliquez sur "Payer avec Orange Money"', 'Vous serez redirigé vers la page sécurisée Orange Money', 'Entrez votre numéro Orange Money et votre code PIN', 'Confirmez avec le code OTP reçu par SMS', '✅ Votre abonnement est activé immédiatement !'].map((step, i) => (
                      <li key={i} className="flex items-start gap-2">
                        <span className="flex-shrink-0 w-6 h-6 rounded-full bg-orange-500 text-white flex items-center justify-center text-xs font-bold">{i + 1}</span>
                        <span>{step}</span>
                      </li>
                    ))}
                  </ol>
                </div>
                <div className="flex items-center gap-2 text-sm text-white/60">
                  <AlertCircle className="w-4 h-4 text-orange-400" />
                  <span>Aucune information bancaire n'est stockée sur nos serveurs</span>
                </div>
              </div>
            )}

            {/* STRIPE */}
            {paymentMethod === 'stripe' && showStripeForm && (
              <div className="mb-6">
                <StripePaymentForm
                  cart={cart}
                  getTotalAmount={getTotalAmount}
                  onSuccess={() => {
                    alert('✅ Paiement réussi ! Votre abonnement est activé.');
                    setCart([]);
                    setShowStripeForm(false);
                    loadData();
                    onBack();
                  }}
                  onCancel={() => setShowStripeForm(false)}
                />
              </div>
            )}

            {/* ═══════════════════════════════════════════════════ */}
            {/* BOUTON PAYER (masqué pour mvola step 2 et succès) */}
            {/* ═══════════════════════════════════════════════════ */}
            {!(paymentMethod === 'mvola' && (mvolaStep === 2 || mvolaSuccess)) && (
              <button
                onClick={processPayment}
                disabled={subscribing}
                className="w-full py-4 rounded-xl bg-gradient-to-r from-purple-600 via-pink-600 to-cyan-600 hover:from-purple-700 hover:via-pink-700 hover:to-cyan-700 text-white font-bold text-lg transition-all hover:scale-105 shadow-2xl disabled:opacity-50 disabled:cursor-not-allowed disabled:scale-100 flex items-center justify-center gap-2"
              >
                {subscribing ? (
                  <span className="flex items-center justify-center gap-2">
                    <div className="w-5 h-5 border-2 border-white/30 border-t-white rounded-full animate-spin" />
                    Redirection vers Orange Money...
                  </span>
                ) : (
                  <>
                    {paymentMethod === 'orange_money' ? (
                      <>
                        <img src="/orange.png" alt="Orange Money" className="w-6 h-6" />
                        Payer avec Orange Money • {getTotalAmount().toLocaleString()} Ar
                      </>
                    ) : paymentMethod === 'mvola' ? (
                      <>📱 Continuer avec MVola • {getTotalAmount().toLocaleString()} Ar</>
                    ) : (
                      <>💳 Payer {getTotalAmount().toLocaleString()} Ar</>
                    )}
                  </>
                )}
              </button>
            )}

            <p className="text-center text-sm text-white/40 mt-4">
              🔒 Paiement sécurisé • Notre équipe vous contactera sous 24h
            </p>
          </div>
        )}

        {cart.length === 0 && (
          <div className="text-center py-12">
            <ShoppingCart className="w-20 h-20 mx-auto mb-4 text-white/20" />
            <p className="text-white/60 text-lg">Sélectionnez un service pour commencer</p>
          </div>
        )}
      </div>

      {showConfigModal && (
        <ConfigModal
          service={currentConfigService}
          facebookPages={facebookPages}
          config={serviceConfig[currentConfigService?.id] || {}}
          onConfigChange={(newConfig) => setServiceConfig({ ...serviceConfig, [currentConfigService.id]: newConfig })}
          onConfirm={confirmServiceConfig}
          onClose={() => { setShowConfigModal(false); setCurrentConfigService(null); }}
          calculateServicePrice={calculateServicePrice}
        />
      )}
    </div>
  );
}

// ═══════════════════════════════════════════════════════════════
// ✅ COMPOSANT: INSTRUCTIONS MVOLA (STEP 1)
// ═══════════════════════════════════════════════════════════════
function MvolaInstructions({ totalAmount, mvolaNumber, mvolaNumberClean, mvolaName, mvolaCopied, onCopy, onContinue }) {
  return (
    <div className="rounded-2xl overflow-hidden border border-yellow-500/30 shadow-2xl">
      {/* Header */}
      <div className="bg-gradient-to-r from-yellow-500/20 to-orange-500/20 px-6 py-5 border-b border-yellow-500/20">
        <div className="flex items-center gap-3">
          <div className="w-12 h-12 rounded-full bg-gradient-to-br from-yellow-400 to-orange-500 flex items-center justify-center shadow-lg">
            <img src="/mvola.png" alt="MVola" className="w-8 h-8 object-contain" onError={(e) => { e.target.style.display = 'none'; }} />
          </div>
          <div>
            <h4 className="text-white font-bold text-lg">Paiement MVola</h4>
            <p className="text-yellow-300/80 text-sm">Transfert direct vers notre compte</p>
          </div>
          <div className="ml-auto text-right">
            <div className="text-white/60 text-sm">À payer</div>
            <div className="text-2xl font-bold text-transparent bg-gradient-to-r from-yellow-400 to-orange-400 bg-clip-text">
              {totalAmount.toLocaleString()} Ar
            </div>
          </div>
        </div>
      </div>

      {/* Steps */}
      <div className="bg-black/30 px-6 py-6 space-y-4">

        {/* Step 1 */}
        <div className="flex gap-4">
          <div className="flex-shrink-0 w-9 h-9 rounded-full bg-gradient-to-br from-yellow-400 to-orange-500 flex items-center justify-center font-bold text-black text-sm shadow-md">
            1
          </div>
          <div className="flex-1">
            <p className="text-white font-semibold mb-3">
              Effectuez le transfert MVola vers notre numéro :
            </p>
            {/* Numéro à copier */}
            <div className="bg-gradient-to-r from-yellow-500/10 to-orange-500/10 border-2 border-yellow-500/40 rounded-xl p-4">
              <div className="flex items-center justify-between gap-3">
                <div>
                  <div className="text-3xl font-bold text-white tracking-widest mb-1">
                    {mvolaNumber}
                  </div>
                  <div className="flex items-center gap-2">
                    <div className="w-2 h-2 rounded-full bg-green-400 animate-pulse" />
                    <span className="text-yellow-300 font-semibold text-sm">{mvolaName}</span>
                  </div>
                </div>
                <button
                  onClick={onCopy}
                  className={`flex flex-col items-center gap-1 px-4 py-3 rounded-xl border-2 transition-all font-bold text-sm ${
                    mvolaCopied
                      ? 'border-green-500 bg-green-500/20 text-green-400 scale-95'
                      : 'border-yellow-500/50 bg-yellow-500/10 text-yellow-300 hover:bg-yellow-500/20 hover:scale-105 active:scale-95'
                  }`}
                >
                  {mvolaCopied ? (
                    <><CheckCircle className="w-5 h-5" /><span>Copié !</span></>
                  ) : (
                    <><Copy className="w-5 h-5" /><span>Copier</span></>
                  )}
                </button>
              </div>
              {/* Montant exact */}
              <div className="mt-3 pt-3 border-t border-yellow-500/20 flex items-center justify-between">
                <span className="text-white/60 text-sm">Montant exact à envoyer :</span>
                <span className="text-yellow-300 font-bold text-lg">{totalAmount.toLocaleString()} Ar</span>
              </div>
            </div>
          </div>
        </div>

        {/* Step 2 */}
        <div className="flex gap-4">
          <div className="flex-shrink-0 w-9 h-9 rounded-full bg-gradient-to-br from-yellow-400 to-orange-500 flex items-center justify-center font-bold text-black text-sm shadow-md">
            2
          </div>
          <div className="flex-1">
            <p className="text-white font-semibold mb-1">Notez la référence du transfert</p>
            <p className="text-white/60 text-sm">
              Après le transfert, vous recevrez un SMS de confirmation MVola avec un numéro de référence. Conservez-le.
            </p>
          </div>
        </div>

        {/* Step 3 */}
        <div className="flex gap-4">
          <div className="flex-shrink-0 w-9 h-9 rounded-full bg-gradient-to-br from-yellow-400 to-orange-500 flex items-center justify-center font-bold text-black text-sm shadow-md">
            3
          </div>
          <div className="flex-1">
            <p className="text-white font-semibold mb-1">Soumettez la référence ici</p>
            <p className="text-white/60 text-sm">
              Cliquez sur "J'ai effectué le paiement" pour saisir votre référence. Notre équipe vérifiera et activera votre service sous <span className="text-green-400 font-semibold">24h</span>.
            </p>
          </div>
        </div>

        {/* Notice */}
        <div className="flex items-start gap-3 bg-blue-500/10 border border-blue-500/30 rounded-xl p-4">
          <AlertCircle className="w-5 h-5 text-blue-400 flex-shrink-0 mt-0.5" />
          <p className="text-blue-200 text-sm">
            Vérifiez bien que le montant et le destinataire sont corrects avant de confirmer le transfert dans votre application MVola.
          </p>
        </div>
      </div>

      {/* CTA */}
      <div className="bg-black/20 px-6 py-5">
        <button
          onClick={onContinue}
          className="w-full py-4 rounded-xl bg-gradient-to-r from-yellow-500 to-orange-500 hover:from-yellow-400 hover:to-orange-400 text-black font-bold text-lg transition-all hover:scale-105 active:scale-95 shadow-lg flex items-center justify-center gap-2"
        >
          <CheckCircle className="w-5 h-5" />
          J'ai effectué le paiement →
        </button>
        <p className="text-center text-white/40 text-xs mt-3">
          Cliquez seulement après avoir réalisé le transfert MVola
        </p>
      </div>
    </div>
  );
}

// ═══════════════════════════════════════════════════════════════
// ✅ COMPOSANT: FORMULAIRE CONFIRMATION MVOLA (STEP 2)
// ═══════════════════════════════════════════════════════════════
function MvolaConfirmForm({ mvolaRef, setMvolaRef, mvolaClientPhone, setMvolaClientPhone, totalAmount, mvolaNumber, mvolaName, onBack, onSubmit, submitting }) {
  const formatPhone = (value) => {
    let v = value.replace(/\D/g, '');
    if (v.length > 10) v = v.slice(0, 10);
    if (v.length >= 3) v = v.slice(0, 3) + ' ' + v.slice(3);
    if (v.length >= 7) v = v.slice(0, 6) + ' ' + v.slice(6);
    if (v.length >= 10) v = v.slice(0, 9) + ' ' + v.slice(9);
    return v;
  };

  return (
    <div className="rounded-2xl overflow-hidden border border-yellow-500/30 shadow-2xl">
      {/* Header */}
      <div className="bg-gradient-to-r from-yellow-500/20 to-orange-500/20 px-6 py-5 border-b border-yellow-500/20">
        <div className="flex items-center gap-3">
          <button
            onClick={onBack}
            className="w-9 h-9 rounded-lg bg-white/10 hover:bg-white/20 text-white flex items-center justify-center transition-all"
          >
            <ArrowLeft className="w-4 h-4" />
          </button>
          <div>
            <h4 className="text-white font-bold text-lg">Confirmation du paiement</h4>
            <p className="text-yellow-300/80 text-sm">Entrez la référence de votre transfert</p>
          </div>
          <div className="ml-auto">
            <div className="text-2xl font-bold text-transparent bg-gradient-to-r from-yellow-400 to-orange-400 bg-clip-text">
              {totalAmount.toLocaleString()} Ar
            </div>
          </div>
        </div>
      </div>

      <div className="bg-black/30 px-6 py-6 space-y-5">
        {/* Rappel destinataire */}
        <div className="flex items-center gap-3 bg-green-500/10 border border-green-500/30 rounded-xl p-4">
          <div className="w-8 h-8 rounded-full bg-green-500/20 flex items-center justify-center">
            <CheckCircle className="w-5 h-5 text-green-400" />
          </div>
          <div>
            <div className="text-white/60 text-xs">Paiement envoyé vers</div>
            <div className="text-white font-bold">{mvolaNumber} — <span className="text-green-400">{mvolaName}</span></div>
          </div>
        </div>

        {/* Champ référence */}
        <div>
          <label className="block text-white font-bold mb-2 flex items-center gap-2">
            <span className="w-7 h-7 rounded-full bg-yellow-500/20 border border-yellow-500/50 flex items-center justify-center text-yellow-400 text-xs font-bold">1</span>
            Référence du transfert MVola *
          </label>
          <input
            type="text"
            placeholder="ex: MG2024123456789..."
            value={mvolaRef}
            onChange={(e) => setMvolaRef(e.target.value.toUpperCase())}
            className="w-full px-4 py-4 bg-white/10 border-2 border-yellow-500/30 rounded-xl text-white font-mono text-lg focus:outline-none focus:border-yellow-400 placeholder-white/30 transition-all"
          />
          <p className="text-white/50 text-xs mt-2 flex items-center gap-1">
            <AlertCircle className="w-3 h-3" />
            Numéro de référence reçu par SMS après votre transfert MVola
          </p>
        </div>

        {/* Champ téléphone */}
        <div>
          <label className="block text-white font-bold mb-2 flex items-center gap-2">
            <span className="w-7 h-7 rounded-full bg-yellow-500/20 border border-yellow-500/50 flex items-center justify-center text-yellow-400 text-xs font-bold">2</span>
            Votre numéro (contact / WhatsApp) *
          </label>
          <div className="relative">
            <Phone className="absolute left-4 top-1/2 -translate-y-1/2 w-5 h-5 text-white/40" />
            <input
              type="tel"
              placeholder="034 XX XX XXX"
              value={mvolaClientPhone}
              onChange={(e) => setMvolaClientPhone(formatPhone(e.target.value))}
              className="w-full pl-12 pr-4 py-4 bg-white/10 border-2 border-yellow-500/30 rounded-xl text-white text-lg focus:outline-none focus:border-yellow-400 placeholder-white/30 transition-all"
              maxLength={13}
            />
          </div>
          <p className="text-white/50 text-xs mt-2 flex items-center gap-1">
            <MessageCircle className="w-3 h-3 text-green-400" />
            Nous vous enverrons la confirmation d'activation par WhatsApp ou appel
          </p>
        </div>

        {/* Info traitement */}
        <div className="bg-gradient-to-r from-purple-500/10 to-blue-500/10 border border-purple-500/30 rounded-xl p-4">
          <div className="flex items-start gap-3">
            <div className="w-10 h-10 rounded-full bg-purple-500/20 flex items-center justify-center flex-shrink-0">
              <Mail className="w-5 h-5 text-purple-400" />
            </div>
            <div>
              <p className="text-white font-semibold text-sm mb-1">Traitement sous 24h</p>
              <p className="text-white/60 text-xs leading-relaxed">
                Votre demande sera transmise à notre équipe qui vérifiera le paiement et activera votre service. Vous serez contacté(e) dès confirmation.
              </p>
            </div>
          </div>
        </div>
      </div>

      {/* Actions */}
      <div className="bg-black/20 px-6 py-5 flex gap-3">
        <button
          onClick={onBack}
          className="flex-1 py-4 rounded-xl bg-white/10 hover:bg-white/20 text-white font-bold transition-all"
        >
          Retour
        </button>
        <button
          onClick={onSubmit}
          disabled={submitting || !mvolaRef.trim() || !mvolaClientPhone.trim()}
          className="flex-2 w-full py-4 px-6 rounded-xl bg-gradient-to-r from-yellow-500 to-orange-500 hover:from-yellow-400 hover:to-orange-400 disabled:opacity-50 disabled:cursor-not-allowed text-black font-bold text-lg transition-all hover:scale-105 active:scale-95 shadow-lg flex items-center justify-center gap-2"
          style={{ flex: 2 }}
        >
          {submitting ? (
            <>
              <div className="w-5 h-5 border-2 border-black/30 border-t-black rounded-full animate-spin" />
              Envoi en cours...
            </>
          ) : (
            <>
              <Send className="w-5 h-5" />
              Soumettre ma référence
            </>
          )}
        </button>
      </div>
    </div>
  );
}

// ═══════════════════════════════════════════════════════════════
// ✅ COMPOSANT: SUCCÈS MVOLA
// ═══════════════════════════════════════════════════════════════
function MvolaSuccessView({ mvolaRef, onDone }) {
  return (
    <div className="rounded-2xl overflow-hidden border border-green-500/40 shadow-2xl">
      {/* Confetti header */}
      <div className="bg-gradient-to-r from-green-500/20 to-emerald-500/20 px-6 py-8 text-center border-b border-green-500/20">
        <div className="w-20 h-20 rounded-full bg-gradient-to-br from-green-400 to-emerald-500 flex items-center justify-center mx-auto mb-4 shadow-xl shadow-green-500/30">
          <CheckCircle className="w-10 h-10 text-white" />
        </div>
        <h3 className="text-2xl font-bold text-white mb-2">Référence reçue !</h3>
        <p className="text-green-300/80 text-sm">Votre demande a été transmise à notre équipe</p>
      </div>

      <div className="bg-black/30 px-6 py-6 space-y-4">
        {/* Référence */}
        <div className="bg-white/5 rounded-xl p-4 border border-white/10 text-center">
          <div className="text-white/50 text-xs mb-1">Référence enregistrée</div>
          <div className="text-yellow-300 font-mono font-bold text-xl tracking-wider">{mvolaRef}</div>
        </div>

        {/* Steps suivants */}
        <div className="space-y-3">
          {[
            { icon: '🔍', title: 'Vérification', desc: 'Notre équipe vérifie votre paiement MVola', color: 'text-blue-300' },
            { icon: '⚡', title: 'Activation', desc: 'Votre service TeckiA est activé', color: 'text-purple-300' },
            { icon: '📲', title: 'Confirmation', desc: 'Vous êtes contacté(e) par WhatsApp ou appel', color: 'text-green-300' }
          ].map((step, i) => (
            <div key={i} className="flex items-center gap-4 bg-white/5 rounded-xl p-4 border border-white/10">
              <div className="text-2xl">{step.icon}</div>
              <div className="flex-1">
                <div className={`font-bold text-sm ${step.color}`}>{step.title}</div>
                <div className="text-white/60 text-xs">{step.desc}</div>
              </div>
              <div className="w-2 h-2 rounded-full bg-white/20" />
            </div>
          ))}
        </div>

        <div className="bg-yellow-500/10 border border-yellow-500/30 rounded-xl p-4 flex items-center gap-3">
          <AlertCircle className="w-5 h-5 text-yellow-400 flex-shrink-0" />
          <p className="text-white/70 text-sm">
            Délai de traitement : <span className="text-yellow-300 font-bold">moins de 24h</span> en jours ouvrables
          </p>
        </div>
      </div>

      <div className="bg-black/20 px-6 py-5">
        <button
          onClick={onDone}
          className="w-full py-4 rounded-xl bg-gradient-to-r from-purple-600 to-pink-600 hover:from-purple-700 hover:to-pink-700 text-white font-bold text-lg transition-all hover:scale-105 shadow-lg"
        >
          Retour au Dashboard
        </button>
      </div>
    </div>
  );
}

// ═══════════════════════════════════════════════════════════════
// COMPOSANTS INCHANGÉS
// ═══════════════════════════════════════════════════════════════

function ServiceCard({ service, onAdd }) {
  const getPriceDisplay = () => {
    if (service.pricing_type === 'custom') return 'Sur mesure';
    if (service.pricing_type === 'monthly') return `${service.monthly.price.toLocaleString()} Ar/mois`;
    if (service.pricing_type === 'both') return `À partir de ${service.monthly.price.toLocaleString()} Ar/mois`;
    if (service.pricing_type === 'credits') return 'À la carte';
    return 'Prix sur demande';
  };

  const isPending = service.subscription_status === 'pending_setup';
  const isActive = !service.can_subscribe && !isPending;

  return (
    <div
      onClick={() => service.can_subscribe && !isPending && onAdd()}
      className={`relative rounded-2xl p-6 border-2 transition-all duration-300 ${
        isActive ? 'bg-green-500/10 border-green-500/50 cursor-not-allowed opacity-75'
        : isPending ? 'bg-yellow-500/10 border-yellow-500/50 cursor-not-allowed opacity-75'
        : 'bg-gradient-to-br from-white/5 to-white/10 border-white/10 hover:border-purple-500/50 hover:scale-105 cursor-pointer'
      }`}
    >
      {service.best_seller && service.can_subscribe && (
        <div className="absolute -top-3 -right-3 bg-gradient-to-r from-yellow-400 to-orange-500 text-white px-3 py-1 rounded-full text-xs font-bold shadow-lg animate-pulse">⭐ Best Seller</div>
      )}
      {isActive && (
        <div className="absolute top-4 right-4 bg-green-500 text-white text-xs px-3 py-1 rounded-full font-bold flex items-center gap-1">
          <Check className="w-3 h-3" />ACTIF
        </div>
      )}
      {isPending && (
        <div className="absolute top-4 right-4 bg-yellow-500 text-black text-xs px-3 py-1 rounded-full font-bold flex items-center gap-1 animate-pulse">
          <AlertCircle className="w-3 h-3" />EN ATTENTE
        </div>
      )}
      <div className="mb-4">
        <div className="w-16 h-16 mx-auto bg-gradient-to-br from-purple-500 to-pink-500 rounded-2xl flex items-center justify-center text-white overflow-hidden">
          {service.image_url ? <img src={service.image_url} alt={service.name} className="w-full h-full object-cover" /> : <ServiceIconMap icon={service.icon} />}
        </div>
      </div>
      <h3 className="text-xl font-bold text-white mb-2 text-center">{service.name}</h3>
      <p className="text-sm text-white/60 mb-4 text-center min-h-[40px]">{service.description}</p>
      <div className="text-center mb-4">
        <div className="text-2xl font-bold text-transparent bg-gradient-to-r from-purple-400 to-pink-400 bg-clip-text">{getPriceDisplay()}</div>
        {service.installation_fee > 0 && <div className="text-xs text-white/40 mt-1">+ {service.installation_fee.toLocaleString()} Ar installation</div>}
      </div>
      {service.can_subscribe && !isPending ? (
        <div className="w-full py-3 rounded-xl bg-gradient-to-r from-purple-600 to-pink-600 text-white font-bold transition-all text-center">Sélectionner</div>
      ) : isPending ? (
        <div className="w-full py-3 rounded-xl bg-yellow-500/20 text-yellow-300 font-bold text-center border border-yellow-500/30">Configuration en cours</div>
      ) : (
        <div className="w-full py-3 rounded-xl bg-green-500/20 text-green-300 font-bold text-center border border-green-500/30">
          {service.pages_with_service?.length > 0 ? 'Toutes pages actives' : 'Déjà souscrit'}
        </div>
      )}
    </div>
  );
}

function ServiceIconMap({ icon }) {
  const icons = { '🤖': Bot, '🌐': Globe, '💬': MessageCircle, '📱': Smartphone, '🎬': Video, '🎥': Film, '🎨': Sparkles, '🧪': Package };
  const Icon = icons[icon] || Bot;
  return <Icon className="w-8 h-8" />;
}

function CartItem({ item, onRemove }) {
  return (
    <div className="bg-white/5 rounded-xl p-4 border border-white/10">
      <div className="flex items-start justify-between gap-4">
        <div className="flex-1">
          <div className="flex items-center gap-3 mb-2">
            <div className="w-10 h-10 bg-gradient-to-br from-purple-500 to-pink-500 rounded-lg flex items-center justify-center text-white overflow-hidden">
              {item.service.image_url ? <img src={item.service.image_url} alt={item.service.name} className="w-full h-full object-cover" /> : <ServiceIconMap icon={item.service.icon} />}
            </div>
            <div>
              <div className="text-white font-bold">{item.service.name}</div>
              <div className="text-xs text-white/60">
                {item.config.pricing_type === 'monthly' ? 'Forfait Mensuel' :
                 item.config.pricing_type === 'quarterly' ? 'Forfait Trimestriel' :
                 item.config.pricing_type === 'annual' ? 'Forfait Annuel' :
                 item.config.pricing_type === 'custom' ? 'Sur mesure' : 'Paiement par Crédits'}
              </div>
            </div>
          </div>
          {item.config.facebook_page_id && <div className="text-xs text-white/60 ml-13">📄 Page: {item.config.facebook_page_id}</div>}
          {item.config.contact_phone && <div className="text-xs text-white/60 ml-13">📞 Contact: {item.config.contact_phone}</div>}
          {item.config.pricing_type === 'credits' && (
            <div className="ml-13 mt-2 space-y-1">
              {item.service.id === 'video_ads' && item.config.credit_quantity && (
                <div className="text-xs font-bold">
                  <span className="text-purple-300">🎫 {item.config.credit_quantity} crédit(s) vidéos</span>
                  <span className="text-white/40 mx-1">•</span>
                  <span className="text-white/70">{item.config.credit_quantity * 5} vidéos</span>
                </div>
              )}
              {item.service.id === 'marketing_studio' && (
                <>
                  {(item.config.credit_quantity_images || 0) > 0 && <div className="text-xs font-bold"><span className="text-pink-300">📸 {item.config.credit_quantity_images} crédit(s) images</span><span className="text-white/40 mx-1">•</span><span className="text-white/70">{item.config.credit_quantity_images * 10} images</span></div>}
                  {(item.config.credit_quantity_videos || 0) > 0 && <div className="text-xs font-bold"><span className="text-cyan-300">🎬 {item.config.credit_quantity_videos} crédit(s) vidéos</span><span className="text-white/40 mx-1">•</span><span className="text-white/70">{item.config.credit_quantity_videos * 10} vidéos</span></div>}
                </>
              )}
              {item.service.id === 'teckia_vtryon' && item.config.credit_quantity && (
                <div className="text-xs font-bold">
                  {item.config.credits_type === 'images' ? <><span className="text-purple-300">👗 {item.config.credit_quantity} crédit(s) images</span><span className="text-white/40 mx-1">•</span><span className="text-white/70">{item.config.credit_quantity * 20} images</span></> : <><span className="text-purple-300">🎥 {item.config.credit_quantity} crédit(s) vidéos</span><span className="text-white/40 mx-1">•</span><span className="text-white/70">{item.config.credit_quantity * 10} vidéos</span></>}
                </div>
              )}
            </div>
          )}
        </div>
        <div className="text-right">
          <div className="text-white font-bold text-lg">{item.price.total.toLocaleString()} Ar</div>
          <button onClick={onRemove} className="mt-2 p-2 rounded-lg bg-red-500/20 hover:bg-red-500/30 text-red-300 transition-all"><X className="w-4 h-4" /></button>
        </div>
      </div>
    </div>
  );
}

// ConfigModal inchangée (identique à l'original)
function ConfigModal({ service, facebookPages, config, onConfigChange, onConfirm, onClose, calculateServicePrice }) {
  if (!service) return null;
  const updateConfig = (key, value) => onConfigChange({ ...config, [key]: value });

  const getAvailablePages = () => {
    if (service.id !== 'teckia_vtryon') return facebookPages;
    const pagesWithService = service.pages_with_service || [];
    return facebookPages.map(page => ({ ...page, hasService: pagesWithService.includes(page.$id), isAvailable: !pagesWithService.includes(page.$id) }));
  };

  const availablePages = getAvailablePages();

  return (
    <div className="fixed inset-0 bg-black/80 backdrop-blur-sm flex items-center justify-center z-50 p-4 overflow-y-auto">
      <div className="bg-gradient-to-br from-slate-900 to-purple-900 rounded-3xl p-6 md:p-8 max-w-2xl w-full my-8 border border-purple-500/30 max-h-[90vh] overflow-y-auto">
        <div className="flex items-center justify-between mb-6">
          <h2 className="text-2xl font-bold text-white">Configuration</h2>
          <button onClick={onClose} className="p-2 hover:bg-white/10 rounded-lg transition-all"><X className="w-6 h-6 text-white" /></button>
        </div>

        <div className="mb-6">
          <div className="flex items-center gap-3 mb-4">
            <div className="w-12 h-12 bg-gradient-to-br from-purple-500 to-pink-500 rounded-xl flex items-center justify-center text-white overflow-hidden">
              {service.image_url ? <img src={service.image_url} alt={service.name} className="w-full h-full object-cover" /> : <ServiceIconMap icon={service.icon} />}
            </div>
            <div>
              <h3 className="text-xl font-bold text-white">{service.name}</h3>
              <p className="text-sm text-white/60">{service.description}</p>
            </div>
          </div>
        </div>

        {service.requires_facebook_page && (
          <div className="mb-6">
            <label className="block text-white font-bold mb-2">Sélectionnez une page Facebook *</label>
            <select value={config.facebook_page_id || ''} onChange={(e) => updateConfig('facebook_page_id', e.target.value)} className="w-full px-4 py-3 bg-white/10 border border-white/20 rounded-xl text-white focus:outline-none focus:border-purple-400">
              <option value="">-- Choisir une page --</option>
              {availablePages.map(page => <option key={page.$id} value={page.$id} disabled={page.hasService} className="bg-slate-900">{page.name} {page.hasService ? '✓ (Déjà souscrit)' : ''}</option>)}
            </select>
            {service.id === 'teckia_vtryon' && service.pages_with_service?.length > 0 && (
              <div className="mt-2 p-3 bg-green-500/10 border border-green-500/30 rounded-lg">
                <div className="text-xs text-green-300 flex items-center gap-2"><Check className="w-4 h-4" /><span>{service.pages_with_service.length} page(s) déjà active(s). Sélectionnez une nouvelle page pour souscrire à nouveau.</span></div>
              </div>
            )}
          </div>
        )}

        {service.requires_phone_setup && (
          <div className="mb-6 bg-yellow-500/10 border border-yellow-500/30 rounded-xl p-4">
            <label className="block text-white font-bold mb-2 flex items-center gap-2"><Phone className="w-5 h-5 text-yellow-400" />Numéro de contact *</label>
            <input type="tel" placeholder="034 XX XX XXX" value={config.contact_phone || ''} onChange={(e) => { let v = e.target.value.replace(/\D/g,''); if(v.length>10)v=v.slice(0,10); if(v.length>=3)v=v.slice(0,3)+' '+v.slice(3); if(v.length>=7)v=v.slice(0,6)+' '+v.slice(6); if(v.length>=10)v=v.slice(0,9)+' '+v.slice(9); updateConfig('contact_phone', v); }} className="w-full px-4 py-3 bg-white/10 border border-white/20 rounded-xl text-white focus:outline-none focus:border-yellow-400" maxLength={13} />
            <p className="text-xs text-white/60 mt-2">Notre équipe vous contactera pour la mise en place</p>
          </div>
        )}

        <div className="mb-6">
          <label className="block text-white font-bold mb-3">Type de tarification *</label>
          <div className="space-y-3">
            {(service.pricing_type === 'monthly' || service.pricing_type === 'both') && (
              <button type="button" onClick={(e) => { e.preventDefault(); e.stopPropagation(); onConfigChange({ ...config, pricing_type: 'monthly', credits_type: null, credit_quantity: null, credit_quantity_images: null, credit_quantity_videos: null }); }} className={`w-full p-4 rounded-xl border-2 text-left transition-all cursor-pointer ${config.pricing_type === 'monthly' ? 'border-purple-500 bg-purple-500/20' : 'border-white/10 bg-white/5 hover:border-white/20 hover:bg-white/10'}`}>
                <div className="flex items-center justify-between"><div><div className="text-white font-bold">Forfait Mensuel</div><div className="text-sm text-white/60">{service.monthly.price.toLocaleString()} Ar/mois</div></div>{config.pricing_type === 'monthly' && <Check className="w-6 h-6 text-purple-400" />}</div>
              </button>
            )}
            {service.id === 'teckia_vtryon' && service.quarterly && (
              <button type="button" onClick={(e) => { e.preventDefault(); e.stopPropagation(); onConfigChange({ ...config, pricing_type: 'quarterly', credits_type: null, credit_quantity: null, credit_quantity_images: null, credit_quantity_videos: null }); }} className={`w-full p-4 rounded-xl border-2 text-left transition-all cursor-pointer ${config.pricing_type === 'quarterly' ? 'border-purple-500 bg-purple-500/20' : 'border-white/10 bg-white/5 hover:border-white/20 hover:bg-white/10'}`}>
                <div className="flex items-center justify-between"><div><div className="text-white font-bold">Forfait Trimestriel</div><div className="text-sm text-white/60">{service.quarterly.price.toLocaleString()} Ar / 3 mois</div></div>{config.pricing_type === 'quarterly' && <Check className="w-6 h-6 text-purple-400" />}</div>
              </button>
            )}
            {service.id === 'teckia_vtryon' && service.annual && (
              <button type="button" onClick={(e) => { e.preventDefault(); e.stopPropagation(); onConfigChange({ ...config, pricing_type: 'annual', credits_type: null, credit_quantity: null, credit_quantity_images: null, credit_quantity_videos: null }); }} className={`w-full p-4 rounded-xl border-2 text-left transition-all cursor-pointer ${config.pricing_type === 'annual' ? 'border-purple-500 bg-purple-500/20' : 'border-white/10 bg-white/5 hover:border-white/20 hover:bg-white/10'}`}>
                <div className="flex items-center justify-between"><div><div className="text-white font-bold">Forfait Annuel</div><div className="text-sm text-white/60">{service.annual.price.toLocaleString()} Ar / an</div></div>{config.pricing_type === 'annual' && <Check className="w-6 h-6 text-purple-400" />}</div>
              </button>
            )}
            {(service.pricing_type === 'credits' || service.pricing_type === 'both') && service.pricing_type !== 'custom' && (
              <button type="button" onClick={(e) => { e.preventDefault(); e.stopPropagation(); onConfigChange({ ...config, pricing_type: 'credits' }); }} className={`w-full p-4 rounded-xl border-2 text-left transition-all cursor-pointer ${config.pricing_type === 'credits' ? 'border-purple-500 bg-purple-500/20' : 'border-white/10 bg-white/5 hover:border-white/20 hover:bg-white/10'}`}>
                <div className="flex items-center justify-between"><div><div className="text-white font-bold">Paiement par Crédits</div><div className="text-sm text-white/60">{service.id === 'video_ads' ? 'À partir de 15,000 Ar/crédit (5 vidéos)' : service.id === 'marketing_studio' ? 'Images: 8,000 Ar • Vidéos: 18,500 Ar • Combinables !' : service.id === 'teckia_vtryon' ? 'Images: 12,000 Ar • Vidéos: 10,000 Ar' : 'À la carte'}</div></div>{config.pricing_type === 'credits' && <Check className="w-6 h-6 text-purple-400" />}</div>
              </button>
            )}
            {service.pricing_type === 'custom' && (
              <div className="w-full p-6 rounded-xl border-2 border-yellow-500/50 bg-yellow-500/10 text-left">
                <div className="text-white font-bold mb-2 flex items-center gap-2"><Phone className="w-5 h-5 text-yellow-400" />Devis personnalisé</div>
                <div className="text-sm text-white/80">{service.custom?.description || 'Notre équipe vous contactera sous 24h pour établir un devis sur mesure'}</div>
                <div className="mt-4 text-xs text-white/60">📞 Entrez votre numéro de contact ci-dessus</div>
              </div>
            )}
          </div>
        </div>

        {config.pricing_type === 'credits' && (
          <div className="mb-6 bg-gradient-to-br from-purple-500/10 to-pink-500/10 border border-purple-500/30 rounded-xl p-6">
            <h3 className="text-white font-bold mb-4 text-lg flex items-center gap-2"><Sparkles className="w-5 h-5 text-purple-400" />Configuration des crédits</h3>
            {service.id === 'video_ads' && (
              <>
                <div className="bg-purple-500/20 rounded-lg p-4 mb-4 border border-purple-500/30 text-center"><div className="text-white font-bold mb-1">📹 Vidéos Grok Imagine</div><div className="text-sm text-white/60">Créez des vidéos générées par IA à partir de vos images</div></div>
                <label className="block text-white font-semibold mb-3">Quantité de crédits</label>
                <div className="flex items-center gap-3 mb-4">
                  <button type="button" onClick={() => updateConfig('credit_quantity', Math.max(1,(config.credit_quantity||1)-1))} className="w-12 h-12 rounded-xl bg-white/10 hover:bg-white/20 active:scale-95 text-white font-bold text-2xl transition-all flex items-center justify-center">−</button>
                  <input type="number" min="1" value={config.credit_quantity||1} onChange={(e)=>updateConfig('credit_quantity',Math.max(1,parseInt(e.target.value)||1))} className="flex-1 px-4 py-4 bg-white/10 border-2 border-white/20 rounded-xl text-white text-center font-bold text-3xl focus:outline-none focus:border-purple-400" />
                  <button type="button" onClick={() => updateConfig('credit_quantity',(config.credit_quantity||1)+1)} className="w-12 h-12 rounded-xl bg-white/10 hover:bg-white/20 active:scale-95 text-white font-bold text-2xl transition-all flex items-center justify-center">+</button>
                </div>
                <div className="bg-gradient-to-r from-purple-500/20 to-pink-500/20 rounded-xl p-5 text-center border-2 border-purple-500/40">
                  <div className="text-sm text-white/80 mb-2">Prix total</div>
                  <div className="text-4xl font-bold text-white mb-3">{((config.credit_quantity||1)*15000).toLocaleString()} Ar</div>
                  <div className="text-base text-purple-300 font-semibold mb-2">📹 Vous recevrez <span className="text-white text-xl">{(config.credit_quantity||1)*5}</span> vidéos</div>
                  <div className="text-xs text-white/60">1 crédit = 15,000 Ar = 5 vidéos Grok Imagine</div>
                </div>
              </>
            )}
            {service.id === 'marketing_studio' && (
              <>
                <div className="bg-gradient-to-r from-blue-500/10 to-purple-500/10 rounded-lg p-4 mb-6 border border-blue-500/30">
                  <div className="flex items-center gap-2 mb-2"><Sparkles className="w-5 h-5 text-blue-400" /><div className="text-white font-bold">Combinez images ET vidéos !</div></div>
                  <div className="text-sm text-white/70">Choisissez la quantité de chaque type selon vos besoins</div>
                </div>
                {[{type:'images',emoji:'📸',label:'Images',sub:'1 crédit = 10 images Nano Banana = 8,000 Ar',price:8000,field:'credit_quantity_images',from:'pink',perUnit:10,unitLabel:'images'},{type:'videos',emoji:'🎬',label:'Vidéos',sub:'1 crédit = 10 vidéos Veo 3.1 = 18,500 Ar',price:18500,field:'credit_quantity_videos',from:'cyan',perUnit:10,unitLabel:'vidéos'}].map(item => (
                  <div key={item.type} className="mb-6">
                    <div className="flex items-center gap-3 mb-3">
                      <div className={`flex-shrink-0 w-12 h-12 rounded-xl bg-gradient-to-br from-${item.from}-500 to-purple-500 flex items-center justify-center text-2xl`}>{item.emoji}</div>
                      <div className="flex-1"><div className="text-white font-bold text-lg">Crédits {item.label}</div><div className="text-xs text-white/60">{item.sub}</div></div>
                    </div>
                    <div className="flex items-center gap-3">
                      <button type="button" onClick={() => updateConfig(item.field, Math.max(0,(config[item.field]||0)-1))} className="w-10 h-10 rounded-lg bg-white/10 hover:bg-white/20 active:scale-95 text-white font-bold text-xl transition-all flex items-center justify-center">−</button>
                      <input type="number" min="0" value={config[item.field]||0} onChange={(e)=>updateConfig(item.field,Math.max(0,parseInt(e.target.value)||0))} className="flex-1 px-4 py-3 bg-white/10 border-2 border-white/20 rounded-lg text-white text-center font-bold text-2xl focus:outline-none" />
                      <button type="button" onClick={() => updateConfig(item.field,(config[item.field]||0)+1)} className="w-10 h-10 rounded-lg bg-white/10 hover:bg-white/20 active:scale-95 text-white font-bold text-xl transition-all flex items-center justify-center">+</button>
                      <div className="flex-shrink-0 w-32 text-right"><div className="text-white font-bold text-lg">{((config[item.field]||0)*item.price).toLocaleString()} Ar</div><div className={`text-xs text-${item.from}-300`}>{(config[item.field]||0)*item.perUnit} {item.unitLabel}</div></div>
                    </div>
                  </div>
                ))}
                <div className="bg-gradient-to-r from-purple-500/20 to-pink-500/20 rounded-xl p-5 border-2 border-purple-500/40 text-center">
                  <div className="text-sm text-white/80 mb-2">Prix total</div>
                  <div className="text-4xl font-bold text-white mb-3">{((config.credit_quantity_images||0)*8000+(config.credit_quantity_videos||0)*18500).toLocaleString()} Ar</div>
                  <div className="space-y-2">
                    {(config.credit_quantity_images||0)>0&&<div className="flex items-center justify-center gap-2 text-sm text-pink-300"><span className="font-semibold">📸 {(config.credit_quantity_images||0)*10} images</span><span className="text-white/40">•</span><span className="text-white/60">{config.credit_quantity_images} crédit(s)</span></div>}
                    {(config.credit_quantity_videos||0)>0&&<div className="flex items-center justify-center gap-2 text-sm text-cyan-300"><span className="font-semibold">🎬 {(config.credit_quantity_videos||0)*10} vidéos</span><span className="text-white/40">•</span><span className="text-white/60">{config.credit_quantity_videos} crédit(s)</span></div>}
                    {(config.credit_quantity_images||0)===0&&(config.credit_quantity_videos||0)===0&&<div className="text-center text-white/50 text-sm">Ajoutez des crédits images ou vidéos</div>}
                  </div>
                </div>
              </>
            )}
            {service.id === 'teckia_vtryon' && (
              <>
                <label className="block text-white font-semibold mb-3">1️⃣ Choisissez le type de crédits</label>
                <div className="grid grid-cols-2 gap-3 mb-6">
                  {[{type:'images',emoji:'👗',label:'Images',sub:'Studio Pro',price:'12,000 Ar/crédit',per:'20 images/crédit'},{type:'videos',emoji:'🎥',label:'Vidéos',sub:'Hailuo/WAN',price:'10,000 Ar/crédit',per:'10 vidéos/crédit'}].map(opt => (
                    <button key={opt.type} type="button" onClick={() => { updateConfig('credits_type', opt.type); if(!config.credit_quantity) updateConfig('credit_quantity',1); }} className={`p-4 rounded-xl border-2 transition-all ${config.credits_type===opt.type?'border-purple-500 bg-purple-500/30 scale-105':'border-white/20 bg-white/5 hover:border-white/30'}`}>
                      <div className="text-3xl mb-2">{opt.emoji}</div>
                      <div className="text-white font-bold text-lg mb-1">{opt.label}</div>
                      <div className="text-xs text-white/70">{opt.sub}</div>
                      <div className="text-sm text-purple-300 font-semibold mt-2">{opt.price}</div>
                      <div className="text-xs text-white/60">{opt.per}</div>
                    </button>
                  ))}
                </div>
                {config.credits_type && (
                  <>
                    <label className="block text-white font-semibold mb-3">2️⃣ Quantité de crédits</label>
                    <div className="flex items-center gap-3 mb-4">
                      <button type="button" onClick={() => updateConfig('credit_quantity',Math.max(1,(config.credit_quantity||1)-1))} className="w-12 h-12 rounded-xl bg-white/10 hover:bg-white/20 active:scale-95 text-white font-bold text-2xl transition-all flex items-center justify-center">−</button>
                      <input type="number" min="1" value={config.credit_quantity||1} onChange={(e)=>updateConfig('credit_quantity',Math.max(1,parseInt(e.target.value)||1))} className="flex-1 px-4 py-4 bg-white/10 border-2 border-white/20 rounded-xl text-white text-center font-bold text-3xl focus:outline-none focus:border-purple-400" />
                      <button type="button" onClick={() => updateConfig('credit_quantity',(config.credit_quantity||1)+1)} className="w-12 h-12 rounded-xl bg-white/10 hover:bg-white/20 active:scale-95 text-white font-bold text-2xl transition-all flex items-center justify-center">+</button>
                    </div>
                    <div className="bg-gradient-to-r from-purple-500/20 to-pink-500/20 rounded-xl p-5 text-center border-2 border-purple-500/40">
                      <div className="text-sm text-white/80 mb-2">Prix total</div>
                      <div className="text-4xl font-bold text-white mb-3">{config.credits_type==='images'?((config.credit_quantity||1)*12000).toLocaleString():((config.credit_quantity||1)*10000).toLocaleString()} Ar</div>
                      <div className="text-base text-purple-300 font-semibold mb-2">{config.credits_type==='images'?<>👗 Vous recevrez <span className="text-white text-xl">{(config.credit_quantity||1)*20}</span> images</>:<>🎥 Vous recevrez <span className="text-white text-xl">{(config.credit_quantity||1)*10}</span> vidéos</>}</div>
                      <div className="text-xs text-white/60">{config.credits_type==='images'?'1 crédit = 12,000 Ar = 20 images Studio Pro':'1 crédit = 10,000 Ar = 10 vidéos Virtual Try-On'}</div>
                    </div>
                  </>
                )}
              </>
            )}
          </div>
        )}

        <div className="bg-white/5 rounded-xl p-4 mb-6 border border-white/10">
          <div className="flex justify-between mb-2 text-white/70"><span>Installation:</span><span>{service.installation_fee.toLocaleString()} Ar</span></div>
          {config.pricing_type && <div className="flex justify-between mb-2 text-white/70"><span>{config.pricing_type==='monthly'?'Abonnement mensuel':config.pricing_type==='quarterly'?'Abonnement trimestriel':config.pricing_type==='annual'?'Abonnement annuel':config.pricing_type==='custom'?'Sur mesure':'Crédits'}:</span><span>{calculateServicePrice(service,config).subscription.toLocaleString()} Ar</span></div>}
          <div className="border-t border-white/20 pt-2 flex justify-between text-white font-bold text-lg"><span>TOTAL:</span><span className="text-transparent bg-gradient-to-r from-purple-400 to-pink-400 bg-clip-text">{calculateServicePrice(service,config).total.toLocaleString()} Ar</span></div>
          {(config.pricing_type==='quarterly'||config.pricing_type==='annual')&&<div className="mt-2 pt-2 border-t border-white/20 text-center text-sm text-green-400">✨ {config.pricing_type==='quarterly'?'Valable 3 mois':'Valable 1 an'}</div>}
        </div>

        <div className="flex gap-3">
          <button onClick={onClose} className="flex-1 py-3 rounded-xl bg-white/10 hover:bg-white/20 text-white font-bold transition-all">Annuler</button>
          <button onClick={onConfirm} className="flex-1 py-3 rounded-xl bg-gradient-to-r from-purple-600 to-pink-600 hover:from-purple-700 hover:to-pink-700 text-white font-bold transition-all hover:scale-105">Ajouter au panier</button>
        </div>
      </div>
    </div>
  );
}